import { AlertTriangleIcon } from 'lucide-react'

import { AmountCell } from '@/components'
import type { CellContext, ColumnDef } from '@/components/ui/table/types'
import type { OutlierTransaction } from '@/modules/projects'
import { amountTone, humanizeText, numberToCurrency } from '@/utils'
import type { CategoryStatsRow } from './types'
import { outlierReasonLabels } from './utils'

export const outlierReasonsColumn: ColumnDef<OutlierTransaction, unknown> = {
  id: 'reasons',
  accessorFn: (row) => outlierReasonLabels(row.reasons),
  header: 'Reason',
  size: 240,
  enableSorting: false,
  cell: (cell) => {
    const labels = cell.getValue() as string
    return (
      <span className="block truncate" title={labels}>
        {labels}
      </span>
    )
  },
}

const OutlierCountCell = (cell: CellContext<CategoryStatsRow, unknown>) => {
  const count = cell.getValue() as number
  if (count === 0) {
    return <span className="block text-muted-foreground">0</span>
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 font-semibold text-red-700 text-xs">
      <AlertTriangleIcon className="size-3" />
      {count}
    </span>
  )
}

const OutlierImpactCell = (cell: CellContext<CategoryStatsRow, unknown>) => {
  const { outlierAmount, outlierCount } = cell.row.original
  if (outlierCount === 0) {
    return <span className="block text-muted-foreground">—</span>
  }
  return (
    <span className={`block truncate font-medium ${amountTone(outlierAmount)}`}>
      {numberToCurrency(outlierAmount)}
    </span>
  )
}

export const categoryStatsColumns: ColumnDef<CategoryStatsRow, unknown>[] = [
  {
    accessorKey: 'category',
    header: 'Category',
    size: 200,
    meta: { isFluid: true },
    cell: (cell) => (
      <span className="block truncate font-medium">
        {humanizeText(cell.getValue() as string)}
      </span>
    ),
  },
  {
    accessorKey: 'count',
    header: 'Transactions',
    size: 120,
  },
  {
    accessorKey: 'upperBound',
    header: 'Outlier threshold',
    size: 150,
    cell: AmountCell,
  },
  {
    accessorKey: 'outlierCount',
    header: 'Outliers',
    size: 110,
    cell: OutlierCountCell,
  },
  {
    id: 'outlierImpact',
    header: 'Outlier impact',
    size: 160,
    cell: OutlierImpactCell,
  },
]
